import { ItemOf } from '@vendure/admin-ui/core';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { SharedModule, TypedBaseListComponent } from '@vendure/admin-ui/core';
import {
  GetBaseProductsDocument,
  GetBaseProductsQuery,
} from '../../../gql/graphql';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { Observable, combineLatest } from 'rxjs';
import { BaseProductSelectedComponent } from '../base-product-selected/base-product-selected.component';

@Component({
  standalone: true,
  templateUrl: './base-product-list.component.html',
  styleUrls: ['./base-product-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SharedModule, CommonModule, BaseProductSelectedComponent],
})
export class BaseProductListComponent
  extends TypedBaseListComponent<typeof GetBaseProductsDocument, 'baseProducts'>
  implements OnInit
{
  // Here we set up the filters that will be available
  // to use in the data table
  readonly filters = this.createFilterCollection()
    .addFilter({
      name: 'name',
      type: { kind: 'text' },
      label: 'name',
      filterField: 'name',
    })
    .addFilter({
      name: 'size',
      type: { kind: 'number' },
      label: 'size',
      filterField: 'size',
    })
    .addFilter({
      name: 'weight',
      type: { kind: 'number' },
      label: 'weight',
      filterField: 'weight',
    })
    .addFilter({
      name: 'price',
      type: { kind: 'number' },
      label: 'price',
      filterField: 'price',
    })
    .connectToRoute(this.route);

  readonly sorts = this.createSortCollection()
    .defaultSort('id', 'DESC')
    .addSort({ name: 'name' })
    .addSort({ name: 'size' })
    .addSort({ name: 'price' })
    .addSort({ name: 'weight' })
    .addSort({ name: 'createdAt' })
    .connectToRoute(this.route);

  activeIndex: Observable<number>;
  selectedItem: Observable<
    ItemOf<GetBaseProductsQuery, 'baseProducts'> | undefined
  >;

  constructor() {
    super();
    super.configure({
      document: GetBaseProductsDocument,
      getItems: data => data.baseProducts,
      setVariables: (skip, take) => ({
        options: {
          skip,
          take,
          filter: {
            name: {
              contains: this.searchTermControl.value ?? undefined,
            },
            ...this.filters.createFilterInput(),
          },
          sort: this.sorts.createSortInput(),
        },
      }),
      refreshListOnChanges: [
        this.filters.valueChanges,
        this.sorts.valueChanges,
      ],
    });
  }

  close() {
    const params = { ...this.route.snapshot.params };
    delete params.contents;
    this.router.navigate(['./', params], {
      relativeTo: this.route,
      queryParamsHandling: 'preserve',
    });
  }

  ngOnInit(): void {
    super.ngOnInit();

    const selectedId = this.route.paramMap.pipe(
      map(pm => pm.get('contents')),
      distinctUntilChanged(),
    );

    this.selectedItem = combineLatest([this.items$, selectedId]).pipe(
      map(([items, selectedId]) => {
        if (selectedId) {
          return items.find(item => item.id === selectedId);
        }
      }),
    );

    this.activeIndex = combineLatest([this.items$, selectedId]).pipe(
      map(([items, selectedId]) => {
        if (selectedId) {
          return items.findIndex(item => item.id === selectedId);
        } else {
          return -1;
        }
      }),
    );
  }
}
